import {DetailsFieldsSection} from '@/components/ui/details-atoms/DetailsAtoms'
import {useTranslation} from '@/translations/i18n'
import {
    TreatmentRequestDiagnosisValidationSchema,
    TreatmentRequestExtended,
    TreatmentRequestScore
} from '@/features/treatment-requests/types'
import {getDirtyFieldsFromFormValues, ObjectKeys} from '@/utilities/helpers'
import {FormProvider, useForm, useFormContext} from 'react-hook-form'
import {TreatmentRequestEditButtons} from '@/features/treatment-requests/components/treatment-request-edit-buttons/TreatmentRequestEditButtons'
import {useTreatmentRequestEditModeView} from '@/features/treatment-requests/hooks/useTreatmentRequestEditModeView'
import {TreatmentRequestDiagnosisForm} from '@/features/treatment-requests/components/treatment-request-diagnosis-form/TreatmentRequestDiagnosisForm'
import {TreatmentRequestDiagnosisDetails} from '@/features/treatment-requests/components/treatment-request-diagnosis-details/TreatmentRequestDiagnosisDetails'
import {useTreatmentRequestFormSubmission} from '@/features/treatment-requests/hooks/useTreatmentRequestFormSubmission'
import {useOutletContext} from 'react-router-dom'
import {useTreatmentRequestDetailsStore} from '@/features/treatment-requests/store/treatmentRequestDetailsStore'
import {remapTreatmentRequestToDefaultValues, remapTreatmentRequestToPayload} from '@/features/treatment-requests/utils'
import {useUpdateTreatmentRequest} from '@/features/treatment-requests/services/useUpdateTreatmentRequest'
import {zodResolver} from '@hookform/resolvers/zod'
import {usePermissions} from '@/features/auth/store/store'
import {z} from 'zod'

const EditTreatmentRequestDiagnosisValidationSchema = TreatmentRequestDiagnosisValidationSchema.omit({
    scores: true
}).and(
    z.object({
        scores: z.array(TreatmentRequestScore).optional()
    })
)
type EditTreatmentRequestDiagnosisValidationSchema = z.infer<typeof EditTreatmentRequestDiagnosisValidationSchema>

export const TreatmentRequestDiagnosisSection = () => {
    const {treatmentRequest} = useOutletContext<{treatmentRequest: TreatmentRequestExtended}>()
    const defaultValues = remapTreatmentRequestToDefaultValues(treatmentRequest)
    const form = useForm<EditTreatmentRequestDiagnosisValidationSchema>({
        mode: 'onBlur',
        resolver: zodResolver(EditTreatmentRequestDiagnosisValidationSchema),
        defaultValues: {
            diseases: defaultValues.diseases,
            diseaseOnsetDate: defaultValues.diseaseOnsetDate,
            scores: treatmentRequest.scores
        }
    })

    return (
        <FormProvider {...form}>
            <TreatmentRequestDiagnosisSectionContent treatmentRequest={treatmentRequest} />
        </FormProvider>
    )
}

const TreatmentRequestDiagnosisSectionContent = ({treatmentRequest}: {treatmentRequest: TreatmentRequestExtended}) => {
    const {t} = useTranslation()
    const form = useFormContext<TreatmentRequestDiagnosisValidationSchema>()
    const {onSuccessSubmit, onErrorSubmit, scrollToTop} = useTreatmentRequestFormSubmission({
        formSchema: TreatmentRequestDiagnosisValidationSchema
    })
    const permissions = usePermissions()
    const setIsErrorBannerVisible = useTreatmentRequestDetailsStore(state => state.setIsErrorBannerVisible)
    const {isEditModeEnabled, isDisabledView} = useTreatmentRequestEditModeView('diagnosis')

    // mutation
    const updateTreatmentRequest = useUpdateTreatmentRequest({
        urlParams: {treatmentRequestId: treatmentRequest.id},
        params: {responseType: 'extended'},
        options: {
            onSuccess: onSuccessSubmit,
            onError: onErrorSubmit
        }
    })

    // actions
    const handleSubmit = form.handleSubmit(
        formValues => {
            const dirtyFormValues = getDirtyFieldsFromFormValues(form.formState.dirtyFields, formValues, [])
            const parsedValues = remapTreatmentRequestToPayload(dirtyFormValues)
            return updateTreatmentRequest.mutate(parsedValues)
        },
        errors => {
            setIsErrorBannerVisible(true)
            console.error(errors)
            scrollToTop()
        }
    )

    return (
        <DetailsFieldsSection
            title={t('commons:diagnosis')}
            isDisabled={isDisabledView}
            headingEndSlot={
                permissions?.requests?.write && (
                    <TreatmentRequestEditButtons
                        groupName="diagnosis"
                        isConfirmDisabled={!ObjectKeys(form.formState.dirtyFields).length}
                        onCancel={form.reset}
                        onConfirm={handleSubmit}
                        isDisabled={treatmentRequest.status == 'accepted' || treatmentRequest.status == 'rejected'}
                    />
                )
            }
        >
            {isEditModeEnabled ? (
                <TreatmentRequestDiagnosisForm serviceType={treatmentRequest.serviceType} />
            ) : (
                <TreatmentRequestDiagnosisDetails treatmentRequest={treatmentRequest} />
            )}
        </DetailsFieldsSection>
    )
}
